// tabs/CreateSessionDrawer.tsx
import { useCallback, useEffect, useState } from 'react';
import { Drawer, Stack, TextInput, Textarea, Group, Button } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { Select } from '@/components';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useAuth } from '@/hooks/useEnrichedAuth';
import {
  useLeaderPortalService,
  useCareSessionTypeService,
  useLocationService,
  CreateCareSession,
  UpdateCareSessionRequest,
  LeaderSessionView,
} from '@actschurch/ccms-sdk';

interface CreateSessionDrawerProps {
  opened: boolean;
  onClose: () => void;
  caseID: number;
  onSaved: () => void;
  /** Pass a session to open the drawer in edit mode; omit/null for create mode. */
  session?: LeaderSessionView | null;
}

interface SessionFormState {
  name: string;
  description: string;
  details: string;
  typeID: number;
  link: string;
  locationID: number | null;
  sessionDateTime: Date | null;
}

const EMPTY_FORM: SessionFormState = {
  name: '',
  description: '',
  details: '',
  typeID: 0,
  link: '',
  locationID: null,
  sessionDateTime: null,
};

// Local Date → SDK's yyyyMMddHHmmss numeric timestamp
function dateToSDKTimestamp(date: Date): number {
  const pad = (n: number) => String(n).padStart(2, '0');
  return Number(
    `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
  );
}

// SDK's yyyyMMddHHmmss numeric timestamp → local Date (inverse of the above,
// used to prefill the form when editing an existing session)
function sdkTimestampToDate(value?: number | null): Date | null {
  if (!value) return null;
  const raw = String(value);
  if (raw.length < 14) return null;
  const year   = Number(raw.slice(0, 4));
  const month  = Number(raw.slice(4, 6)) - 1;
  const day    = Number(raw.slice(6, 8));
  const hour   = Number(raw.slice(8, 10));
  const minute = Number(raw.slice(10, 12));
  const second = Number(raw.slice(12, 14));
  return new Date(year, month, day, hour, minute, second);
}

function sessionToForm(session: LeaderSessionView): SessionFormState {
  return {
    name:            session.name ?? '',
    description:     '',
    details:         '',
    typeID:          session.typeID ?? 0,
    link:            session.link ?? '',
    locationID:      null,
    sessionDateTime: sdkTimestampToDate(session.sessionDateTime),
  };
}

export default function CreateSessionDrawer({ opened, onClose, caseID, onSaved, session }: CreateSessionDrawerProps) {
  const leaderPortalService = useLeaderPortalService();
  const SessionTypes        = useCareSessionTypeService();
  const Locations           = useLocationService();
  const { user } = useAuth();

  const isEdit = !!session;

  const [form, setForm]             = useState<SessionFormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const [types, setTypes]         = useState<{ value: string; label: string }[]>([]);
  const [locations, setLocations] = useState<{ value: string; label: string }[]>([]);
  const [lookupsLoading, setLookupsLoading] = useState(false);

  // ── Lookups for Type + Location — no Leader select anymore, this is always
  // the current logged-in leader's own session. ──────────────────────────────
  const fetchLookups = useCallback(async () => {
    setLookupsLoading(true);
    try {
      const [typeList, locationList] = await Promise.all([
        SessionTypes.getAll(),
        Locations.getAll(),
      ]);
      setTypes(
        (typeList ?? []).map((t: any) => ({ value: String(t.typeID), label: t.name ?? `Type ${t.typeID}` }))
      );
      setLocations(
        (locationList ?? []).map((loc: any) => ({
          value: String(loc.locationID),
          label: loc.name ?? loc.locationName ?? `Location ${loc.locationID}`,
        }))
      );
    } catch (e) {
      console.error('Error fetching session form lookups:', e);
      toast.error('Error', 'Could not load form options.');
    } finally {
      setLookupsLoading(false);
    }
  }, [SessionTypes, Locations]);

  useEffect(() => { if (opened) fetchLookups(); }, [opened, fetchLookups]);

  // Populate the form from the session being edited, or reset it for "create",
  // every time the drawer opens.
  useEffect(() => {
    if (!opened) return;
    setForm(session ? sessionToForm(session) : EMPTY_FORM);
  }, [opened, session]);

  const resetForm = () => setForm(EMPTY_FORM);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error('Missing name', 'Give the session a name before saving.');
      return;
    }
    if (!form.typeID) {
      toast.error('Missing type', 'Choose a session type.');
      return;
    }
    if (!form.sessionDateTime) {
      toast.error('Missing date', 'Choose a date and time for the session.');
      return;
    }
    if (!isEdit && !user?.id) {
      toast.error('Not signed in', 'Could not determine the current leader.');
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit && session) {
        const payload: UpdateCareSessionRequest = {
          name:            form.name,
          description:     form.description || undefined,
          details:         form.details || undefined,
          typeID:          form.typeID,
          link:            form.link || undefined,
          locationID:      form.locationID ?? undefined,
          sessionDateTime: dateToSDKTimestamp(form.sessionDateTime),
        };
        await leaderPortalService.updateSession(session.sessionID, payload);
        toast.success('Session Updated', `${form.name} has been updated.`);
      } else {
        const payload: CreateCareSession = {
          name:            form.name,
          description:     form.description || undefined,
          details:         form.details || undefined,
          typeID:          form.typeID,
          userId:          user!.id,
          link:            form.link || undefined,
          locationID:      form.locationID ?? undefined,
          sessionDateTime: dateToSDKTimestamp(form.sessionDateTime),
          caseIDs:         [caseID],
        };
        await leaderPortalService.addCaseSession(caseID, payload);
        toast.success('Session Created', `${form.name} has been added.`);
      }

      resetForm();
      onSaved();
      onClose();
    } catch (e) {
      console.error('Error saving session:', e);
      toast.error('Error', `Could not ${isEdit ? 'update' : 'create'} the session. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      title={isEdit ? 'Edit Session' : 'Add Session'}
      position="right"
      size="md"
      padding="md"
    >
      <Stack gap="sm">
        <TextInput
          label="Name"
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.currentTarget.value })}
        />
        <Textarea
          label="Description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.currentTarget.value })}
          minRows={2}
        />
        <Textarea
          label="Details"
          value={form.details}
          onChange={(e) => setForm({ ...form, details: e.currentTarget.value })}
          minRows={2}
        />
        <Select
          label="Type"
          placeholder="Select a session type"
          required
          data={types}
          disabled={lookupsLoading}
          value={form.typeID ? String(form.typeID) : null}
          onChange={(value) => setForm({ ...form, typeID: Number(value) || 0 })}
        />
        {/* ⚠️ ASSUMPTION: @mantine/dates (and its DateTimePicker) is already a
            dependency in this project — swap for whatever date input you use
            elsewhere if not. */}
        <DateTimePicker
          label="Date & time"
          placeholder="Pick date and time"
          required
          value={form.sessionDateTime}
          onChange={(value) => setForm({ ...form, sessionDateTime: value })}
        />
        <TextInput
          label="Virtual link"
          placeholder="https://..."
          value={form.link}
          onChange={(e) => setForm({ ...form, link: e.currentTarget.value })}
        />
        <Select
          label="Location"
          placeholder="Select a location (if in person)"
          clearable
          data={locations}
          disabled={lookupsLoading}
          value={form.locationID ? String(form.locationID) : null}
          onChange={(value) => setForm({ ...form, locationID: value ? Number(value) : null })}
        />
      </Stack>

      <Group justify="flex-end" mt="lg">
        <Button variant="subtle" onClick={handleClose}>Cancel</Button>
        <Button loading={submitting} onClick={handleSubmit}>
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </Group>
    </Drawer>
  );
}